import { UpdateContactLocationMutation } from "shared/graphql/contact.mutation"
import { useClient, useMutation } from "urql"

import type { ContactLocation, ContactLocationUpdateInput } from "@/graphql/codegen/graphql"
import { ContactGetQueryDocument } from "@/graphql/codegen/graphql"

export const useUpdateContactLocationPrimary = (
  contactId?: string,
  onSuccess?: () => void
): {
  mutation: typeof mutation
  updateContactLocation: (input: ContactLocationUpdateInput) => Promise<ContactLocation | undefined>
  isLoading: boolean
  error: typeof mutation.error
} => {
  const client = useClient()
  const [mutation, executeMutation] = useMutation<
    {
      contact: {
        updateContactLocation: {
          contactLocationAssociation?: ContactLocation
          error?: { message: string }
        }
      }
    },
    { input: ContactLocationUpdateInput }
  >(UpdateContactLocationMutation)

  const updateContactLocation = async (input: ContactLocationUpdateInput) => {
    // Use network-only policy to ensure we're getting the latest data
    const result = await executeMutation(
      { input },
      {
        requestPolicy: "network-only",
        // This is the key improvement - telling urql to refetch any queries that might be affected
        // by this mutation
        additionalTypenames: ["Contact", "ContactLocation", "Location"],
      }
    )

    if (result.error) {
      throw new Error(result.error.message)
    }

    const updateResult = result.data?.contact.updateContactLocation
    if (updateResult?.error) {
      throw new Error(updateResult.error.message)
    }

    // Alternative approach: refetch the specific contact directly
    const updatedContactId = updateResult?.contactLocationAssociation?.contactId || contactId
    if (updatedContactId) {
      try {
        // Directly refetch the contact details from the server with fresh data
        await client.reexecuteOperation({
          kind: "query",
          query: ContactGetQueryDocument,
          variables: { input: { contactId: updatedContactId } },
          context: {
            requestPolicy: "network-only", // Force a network request, ignore cache
          },
        })
      } catch (error) {
        console.error("Error refetching contact data:", error)
      }
    }

    if (onSuccess) {
      onSuccess()
    }

    // Successful mutation - updateResult contains the updated primary status
    return updateResult?.contactLocationAssociation
  }

  return {
    mutation,
    updateContactLocation,
    isLoading: mutation.fetching,
    error: mutation.error,
  }
}
